import { Menu, Modal, SubscriptionPlan, Utils } from 'common';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
// cherry-pick lodash methods (the right way). DO NOT use import from 'lodash'.
import each from 'lodash/each';
import isString from 'lodash/isString';
import map from 'lodash/map';
import toLower from 'lodash/toLower';
import trimEnd from 'lodash/trimEnd';
import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { ACTION_LINK_TYPE, LeftMenuItems } from './utils/LeftMenuItems';
import { RoutePath } from './utils/RoutePath';

const { MenuPanel, ActionType, ItemType } = Menu;
const { ModalContext } = Modal;
const { Logger, Session, Navigation } = Utils;
const { getUpgradeIconProps, isCostCodesGated, showUpgradePlanModal } =
	SubscriptionPlan;

class LeftMenuView extends Component {
	constructor(props) {
		super(props);

		const subscriberId = Session.subscribe(() => {
			this.buildLeftMenu();
		});

		this.state = {
			menuItems: [],
			subscriberId,
		};
	}

	componentDidMount() {
		this.buildLeftMenu();
	}

	componentDidUpdate(prevProps) {
		if (prevProps?.location?.pathname !== this.props?.location?.pathname) {
			this.buildLeftMenu();
		}
	}

	componentWillUnmount() {
		const { subscriberId } = this.state;
		Session.unsubscribe(subscriberId);
	}

	buildLeftMenu = async () => {
		// get URL pathname
		const { history, flags = {} } = this.props;
		const pathName = history
			? trimEnd(toLower(history.location.pathname), '/')
			: '/';
		const role = Session.getUserRole();

		// per http://jira/browse/WEBAPPS-22022, this key will turn on Store Credits, Reorder, and Image Gallery tabs in Store Orders section if set to true.
		const { CutAtgStoreLinks = false } = Session.getSettings();

		// Get LaunchDarkly feature flag for Labs section
		const showLabs = flags.showLabs === true;

		const menuItems = LeftMenuItems.build(
			// per WEBAPPS-25584, contactInfo page should be displayed by default
			pathName === '' || pathName === '/' ? RoutePath.contactInfo : pathName,
			role,
			CutAtgStoreLinks,
			showLabs
		);

		// Tag Cost Codes with upgrade-icon props.
		// - Trial: icon + trial popover.
		// - New lineup, non-trial, cost_codes AppCap gated: plain icon (no popover);
		//   the row click opens the upgrade modal via actionTriggerHandler.
		const { showModal } = this.context ?? {};
		if (showModal) {
			const upgradeProps = isCostCodesGated()
				? { showUpgradeIcon: true }
				: getUpgradeIconProps('costCodes', { showModal });
			menuItems.forEach(group => {
				group.items?.forEach(item => {
					if (item.title === 'Cost Codes') Object.assign(item, upgradeProps);
				});
			});
		}

		this.setState({ menuItems });
	};

	/**
	 * Check if there's an override ATG token for the route path defined in
	 * `AccountLeftMenuOverride` in the registry. The value is a string of
	 * a list of tokens separated by commas. The token names are case-sensi
	 * -tive that should match paths defined in RoutePath.js. For example,
	 *
	 *                   `changepassword,commprefs`.
	 *
	 * When `true`, the route path should be redirected to an ATG site.
	 * @param {string} routePath The local route path. See RoutePath.js.
	 * @return {boolean}
	 */
	isRoutePathOverriden = routePath => {
		const { AccountLeftMenuOverride } = Session.getSettings();

		// removing '/' from the path
		const routePathToken = isString(routePath) ? routePath.substr(1) : null;

		return isString(AccountLeftMenuOverride)
			? AccountLeftMenuOverride.indexOf(routePathToken) !== -1
			: false;
	};

	handleNavigation = ({ actionType, itemType, data }) => {
		const {
			type, // ACTION_LINK_TYPE
			id,
			groupId,
			routePath, // for 'ROUTE' type
			navigationToken, // for 'NAVIGATION' type
		} = data;
		const { history } = this.props;
		const { menuItems } = this.state;

		if (actionType === ActionType.Click && itemType === ItemType.Badge) {
			if (
				type === ACTION_LINK_TYPE.ROUTE &&
				!this.isRoutePathOverriden(routePath)
			) {
				// refresh the view
				this.setState({
					menuItems: map(menuItems, menuItem => {
						// highlight the group of the selected child item
						menuItem.active = menuItem.id === groupId;

						// expand the group of the selected child item
						menuItem.collapsed = menuItem.id !== groupId;

						// highlight the selected child item
						each(menuItem.items, item => {
							item.active = item.id === id;
						});

						return menuItem;
					}),
				});

				// update URL
				history.push(routePath);
			} else {
				// navigate to the ATG site
				Navigation.navigateTo(`account_${navigationToken}`);
			}
		}
	};

	showCostCodesUpgradeModal = () => {
		const { showModal } = this.context ?? {};
		if (showModal) showUpgradePlanModal(showModal);
	};

	// render a warning modal if the user's form state is dirty - otherwise navigate to menu item
	actionTriggerHandler = (actionType, itemType, data) => {
		const { location } = this.props;
		const { showDirtyStateModal } = this.context;
		const { routePath } = data;

		Logger.saveActionData('Left Menu Item Clicked', { Route: routePath });

		// Per WEBAPPS-24116 we won't try to navigate to current page nor display a modal.
		if (location.pathname !== routePath) {
			// On new lineup (non-trial), clicking Cost Codes when the cost_codes
			// AppCap is disabled should open the upgrade modal instead of
			// navigating. Trial users get the popover flow instead.
			if (routePath === RoutePath.costCodes && isCostCodesGated()) {
				this.showCostCodesUpgradeModal();
				return;
			}

			if (Session.isFormStateDirty) {
				return showDirtyStateModal({
					data: {
						callback: this.handleNavigation,
						actionType,
						itemType,
						data,
					},
				});
			}

			this.handleNavigation({ actionType, itemType, data });
		}
	};

	render() {
		const { menuItems } = this.state;

		return (
			<MenuPanel
				items={menuItems}
				onActionTrigger={this.actionTriggerHandler}
			/>
		);
	}
}

export const LeftMenuViewWithRouter = withLDConsumer()(
	withRouter(LeftMenuView)
);

LeftMenuView.contextType = ModalContext;
